import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiCall from "../utils/apicall";
import { showSuccessToast, showErrorToast } from '../utils/Toast';

export const getTasks = createAsyncThunk(
  "get-tasks/tasks",
  async (ThunkApi) => {
    try {
      const res = await ApiCall.get("/task");
      if (res.data.data.tasks) {
        return res.data.data.tasks.data;
      } else throw new Error("No User Found !");
    } catch (error) {
      console.log(error);
      return ThunkApi.rejectWithValue("Error logging in");
    }
  }
);

export const getRejectionReasons = createAsyncThunk(
  "rejection-reasons/rejections",
  async (ThunkApi) => {
    try {
      const res = await ApiCall.get("/rejected-reasons");
      if (res.data.data.reasons) {
        const allReasons = res.data.data.reasons;
        const reasons = [];
        allReasons.forEach((reason) => {
          reasons.push(...reason.children);
        });
        return reasons;
      } else throw new Error("No reason Found !");
    } catch (error) {
      console.log(error);
      return ThunkApi.rejectWithValue("Error logging in");
    }
  }
);

export const getSingleTasks = createAsyncThunk(
  "get-single-tasks/tasks",
  async (id, ThunkApi) => {
    try {
      const res = await ApiCall.get(`/task/${id}`);
      if (res.data.data.task) {
        return res.data.data.task;
      } else throw new Error("No User Found !");
    } catch (error) {
      console.log(error);
      return ThunkApi.rejectWithValue("Error logging in");
    }
  }
);

export const createTask = createAsyncThunk(
  "createTask/tasks",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.post("/task", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data && res.data.success) {
        if (res.data.data && res.data.data.task) {
          return res.data.data.task;
        } else {
          throw new Error("Invalid response from server");
        }
      } else {
        throw new Error(res.data?.message || "Error creating task");
      }
    } catch (error) {
      // W-25: carry the server's refusal (e.g. "jobId must be an integer.") instead of axios's
      // generic "Request failed with status code 400".
      const serverMessage = error.response?.data?.message;
      console.log("Error creating task:", serverMessage || error.message);
      throw new Error(serverMessage || error.message || "Error creating task");
    }
  }
);

export const updateTasks = createAsyncThunk(
  "updateTasks/tasks",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.put(`/task/${data.id}`, data.formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data && res.data.success) {
        if (res.data.data && res.data.data.task) {
          return res.data.data.task;
        } else {
          throw new Error("Invalid response from server");
        }
      } else {
        throw new Error(res.data?.message || "Error updating task");
      }
    } catch (error) {
      // Surface the server's descriptive message (e.g. the AISC V-02 ack block
      // returns a 409 with an actionable message) instead of axios's generic
      // "Request failed with status code 409".
      const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
      console.log("Error updating task:", serverMsg || error.message);
      throw new Error(serverMsg || error.message || "Error updating task");
    }
  }
);

export const deleteTasks = createAsyncThunk(
  "deleteTasks/tasks",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.delete(`/task/${data}`);
      if (res.data?.success) {
        return data;
      }
      // Codex WARNING fix: server replied 200 but success=false. Reject so the
      // UI doesn't show fake success and the row stays in state.
      return ThunkApi.rejectWithValue(res.data?.message || "Server reported failure");
    } catch (error) {
      // Codex WARNING fix: previously swallowed errors and returned undefined,
      // which fulfilled the action with payload=undefined. Now reject properly.
      return ThunkApi.rejectWithValue(
        error?.response?.data?.message || error?.message || "Failed to delete task"
      );
    }
  }
);

export const tasksSlice = createSlice({
  name: "tasks",
  initialState: {
    tasks: [],
    isTaskLoading: false,
    isSuccess: false,
    rejectionReasons: [],
    currentTask: [],
    errorValue: null,
  },
  extraReducers: (builder) => {
    builder.addCase(getTasks.pending, (state, action) => {
      state.isTaskLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(getTasks.fulfilled, (state, action) => {
      state.tasks = action.payload;
      state.isTaskLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(getTasks.rejected, (state, action) => {
      state.isTaskLoading = false;
      state.isSuccess = false;
      showErrorToast('Failed to load tasks. Please refresh.');
    });
    builder.addCase(getSingleTasks.pending, (state, action) => {
      state.isTaskLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(getSingleTasks.fulfilled, (state, action) => {
      state.currentTask = action.payload;
      state.isTaskLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(getSingleTasks.rejected, (state, action) => {
      state.isTaskLoading = false;
      state.isSuccess = false;
    });
    builder.addCase(deleteTasks.pending, (state, action) => {
      state.isTaskLoading = true;
    });
    builder.addCase(deleteTasks.fulfilled, (state, action) => {
      state.tasks = state.tasks.filter((task) => task.id !== action.payload);
      state.isTaskLoading = false;
      showSuccessToast('Task deleted successfully.');
    });
    builder.addCase(deleteTasks.rejected, (state, action) => {
      state.isTaskLoading = false;
      showErrorToast('Failed to delete task. Please try again.');
    });
    builder.addCase(createTask.pending, (state, action) => {
      state.isTaskLoading = true;
      state.error = null;
    });
    builder.addCase(createTask.fulfilled, (state, action) => {
      state.tasks.unshift(action.payload);
      state.isTaskLoading = false;
      state.errorValue = null;
      showSuccessToast('Task created successfully.');
    });
    builder.addCase(createTask.rejected, (state, action) => {
      state.isTaskLoading = false;
      state.errorValue = action.error.message;
      showErrorToast(`Task not created: ${action.error.message || 'unknown error'}`);
    });
    builder.addCase(updateTasks.pending, (state, action) => {
      state.isTaskLoading = true;
    });
    builder.addCase(updateTasks.fulfilled, (state, action) => {
      const updatedTask = action.payload;
      state.tasks = state.tasks.map((task) => {
        if (task.id === updatedTask.id) {
          return updatedTask;
        }
        return task;
      });
      state.isTaskLoading = false;
      showSuccessToast('Task updated successfully.');
    });
    builder.addCase(updateTasks.rejected, (state, action) => {
      state.isTaskLoading = false;
      state.errorValue = action.error.message;
      // Show the actual server message (e.g. the AISC V-02 ack block) rather
      // than a generic string, so the operator knows to acknowledge + retry.
      showErrorToast(action.error?.message || 'Failed to update task. Please try again.');
    });
    builder.addCase(getRejectionReasons.pending, (state, action) => {
      state.isTaskLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(getRejectionReasons.fulfilled, (state, action) => {
      state.rejectionReasons = action.payload;
      state.isTaskLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(getRejectionReasons.rejected, (state, action) => {
      state.isTaskLoading = false;
      state.isSuccess = false;
    });
  },
});

export default tasksSlice.reducer;
